export interface TimeZone {
  value: string
  label: string
  offset: string
}

export const TIME_ZONES: TimeZone[] = [
  { value: 'America/Los_Angeles', label: 'Los Angeles', offset: 'UTC-8' },
  { value: 'America/New_York', label: 'New York', offset: 'UTC-5' },
  { value: 'America/Sao_Paulo', label: 'São Paulo', offset: 'UTC-3' },
  { value: 'UTC', label: 'UTC', offset: 'UTC+0' },
  { value: 'Europe/Berlin', label: 'Berlin', offset: 'UTC+1' },
  { value: 'Asia/Dubai', label: 'Dubai', offset: 'UTC+4' },
  { value: 'Asia/Kolkata', label: 'Kolkata', offset: 'UTC+5:30' },
  { value: 'Asia/Shanghai', label: 'Shanghai', offset: 'UTC+8' },
  { value: 'Asia/Tokyo', label: 'Tokyo', offset: 'UTC+9' },
  { value: 'Australia/Sydney', label: 'Sydney', offset: 'UTC+10' },
]

export const getUserTimeZone = (): string => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    return tz || 'UTC'
  } catch {
    return 'UTC'
  }
}

const getTimeZoneOffsetMinutes = (timeZone: string, date: Date = new Date()): number => {
  if (timeZone === 'UTC' || timeZone === 'Etc/UTC' || timeZone === 'Etc/GMT') return 0
  // Prefer parsing the canonical short offset (e.g., GMT+02:00) when available
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
    const parts = dtf.formatToParts(date)
    const tzName = parts.find((p) => p.type === 'timeZoneName')?.value || ''
    const match = tzName.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/)
    if (match) {
      const sign = match[1] === '+' ? 1 : -1
      const hours = parseInt(match[2]!, 10)
      const minutes = match[3] ? parseInt(match[3]!, 10) : 0
      const val = sign * (hours * 60 + minutes)
      return Number.isNaN(val) ? 0 : val
    }
  } catch {
    // fall through to arithmetic method
  }

  // Fallback arithmetic method
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
    const parts = dtf.formatToParts(date)
    const map = Object.fromEntries(parts.map((p) => [p.type, p.value])) as Record<string, string>
    const iso = `${map['year']}-${map['month']}-${map['day']}T${map['hour']}:${map['minute']}:${map['second']}.000Z`
    const asUTC = new Date(iso)
    const val = (asUTC.getTime() - date.getTime()) / 60000
    return Number.isNaN(val) ? 0 : val
  } catch {
    return 0
  }
}

export const getClosestSupportedTimeZone = (detectedTimeZone: string): string => {
  if (TIME_ZONES.some((tz) => tz.value === detectedTimeZone)) return detectedTimeZone

  let targetOffset = 0
  try {
    targetOffset = getTimeZoneOffsetMinutes(detectedTimeZone)
  } catch {
    targetOffset = 0
  }

  const pickClosest = (candidates: typeof TIME_ZONES) => {
    let chosen = candidates[0]!.value
    let smallestDiff = Infinity
    let bestOffset = getTimeZoneOffsetMinutes(chosen)
    for (const tz of candidates) {
      const off = getTimeZoneOffsetMinutes(tz.value)
      const diff = Math.abs(off - targetOffset)
      if (diff < smallestDiff || (diff === smallestDiff && Math.abs(off) < Math.abs(bestOffset))) {
        smallestDiff = diff
        bestOffset = off
        chosen = tz.value
      }
    }
    return chosen
  }

  // Prefer same-region matches first (e.g., Europe/* for Europe/Berlin)
  const detectedRegion = detectedTimeZone.split('/')[0]
  const sameRegion = TIME_ZONES.filter((tz) => tz.value.startsWith(`${detectedRegion}/`))
  if (sameRegion.length > 0) {
    return pickClosest(sameRegion)
  }

  // Otherwise, pick the closest globally
  const closest = pickClosest(TIME_ZONES)

  return closest
}
